// =======================================================
// DinoParade — sunset prehistoric horizon with walking dinosaur silhouettes
// =======================================================
//
// Tuned for very wide canvases (10900×540, ~20:1). The composition is laid
// out in pixel space (uResolution) so the horizon, mountain layers, and
// dinosaur scale stay correct regardless of aspect.
//
// Composition (back to front):
//   1. Sunset gradient + sun disc with soft halo
//   2. Distant mountain ridge (low fbm)
//   3. Mid mountain ridge (more contrast)
//   4. Foreground ground line
//   5. Dinosaur silhouettes (sauropod, t-rex, stegosaurus, pterodactyl)
//      built from SDF primitives, each with leg/wing animation and a slow
//      walking drift across the canvas.

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform vec2  uResolution;
  uniform float uWalkSpeed;
  uniform float uDinoScale;
  uniform float uDinoCount;
  uniform float uMountains;
  uniform vec3  uSkyTop;
  uniform vec3  uSkyMid;
  uniform vec3  uSkyBot;
  uniform vec3  uSunColor;
  uniform vec3  uMountainFar;
  uniform vec3  uMountainNear;
  uniform vec3  uGround;
  uniform vec3  uDinoColor;
  uniform vec3  uRimColor;

  ${GLSL_COMMON}

  // ---------- SDF primitives (units: pixels) ----------
  float sdCircle(vec2 p, float r) { return length(p) - r; }

  float sdCapsule(vec2 p, vec2 a, vec2 b, float r) {
    vec2 pa = p - a, ba = b - a;
    float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-4), 0.0, 1.0);
    return length(pa - ba * h) - r;
  }

  float sdEllipse(vec2 p, vec2 ab) {
    // Cheap approximation — good enough for silhouettes.
    return length(p / ab) * min(ab.x, ab.y) - min(ab.x, ab.y);
  }

  float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }

  // ---------- Dinosaur silhouettes ----------
  // All dinos are authored in a local pixel space where the ground is at y=0
  // and they face right by default. Caller applies translation, scale, and
  // optional horizontal flip.

  float dinoSauropod(vec2 q, float legPhase) {
    float s = 1e9;
    // Body
    s = min(s, sdEllipse(q - vec2(0.0, 70.0), vec2(95.0, 42.0)));
    // Long neck — series of capsules along an arc
    s = min(s, sdCapsule(q, vec2(60.0, 90.0),  vec2(105.0, 140.0), 16.0));
    s = min(s, sdCapsule(q, vec2(105.0, 140.0), vec2(140.0, 180.0), 13.0));
    s = min(s, sdCapsule(q, vec2(140.0, 180.0), vec2(165.0, 215.0), 11.0));
    // Head
    s = min(s, sdEllipse(q - vec2(175.0, 222.0), vec2(18.0, 11.0)));
    // Long tail
    s = min(s, sdCapsule(q, vec2(-60.0, 80.0), vec2(-130.0, 100.0), 14.0));
    s = min(s, sdCapsule(q, vec2(-130.0, 100.0), vec2(-185.0, 90.0), 9.0));
    s = min(s, sdCapsule(q, vec2(-185.0, 90.0), vec2(-225.0, 75.0), 5.0));
    // Legs (animated)
    float p = legPhase;
    float fl = sin(p) * 8.0;       // front-left
    float fr = sin(p + 3.14159) * 8.0;
    float bl = sin(p + 1.57) * 8.0;
    float br = sin(p - 1.57) * 8.0;
    s = min(s, sdCapsule(q, vec2( 50.0, 40.0), vec2( 50.0 + fl, 0.0), 11.0));
    s = min(s, sdCapsule(q, vec2( 25.0, 40.0), vec2( 25.0 + fr, 0.0), 11.0));
    s = min(s, sdCapsule(q, vec2(-30.0, 40.0), vec2(-30.0 + bl, 0.0), 12.0));
    s = min(s, sdCapsule(q, vec2(-55.0, 40.0), vec2(-55.0 + br, 0.0), 12.0));
    return s;
  }

  float dinoTRex(vec2 q, float legPhase) {
    float s = 1e9;
    // Bulky body
    s = min(s, sdEllipse(q - vec2(0.0, 75.0), vec2(70.0, 38.0)));
    // Thick raised neck + big head
    s = min(s, sdCapsule(q, vec2(40.0, 95.0), vec2(75.0, 130.0), 18.0));
    s = min(s, sdEllipse(q - vec2(95.0, 138.0), vec2(28.0, 18.0)));
    // Jaw line (subtle)
    s = min(s, sdCapsule(q, vec2(75.0, 125.0), vec2(118.0, 130.0), 6.0));
    // Tiny arms
    s = min(s, sdCapsule(q, vec2(40.0, 72.0), vec2(60.0, 55.0), 5.0));
    // Heavy tapering tail
    s = min(s, sdCapsule(q, vec2(-50.0, 80.0), vec2(-110.0, 95.0), 16.0));
    s = min(s, sdCapsule(q, vec2(-110.0, 95.0), vec2(-170.0, 105.0), 9.0));
    s = min(s, sdCapsule(q, vec2(-170.0, 105.0), vec2(-205.0, 110.0), 4.0));
    // Powerful legs (bipedal)
    float p = legPhase;
    float lA = sin(p) * 14.0;
    float lB = sin(p + 3.14159) * 14.0;
    // Thigh
    s = min(s, sdCapsule(q, vec2(  5.0, 70.0), vec2(  5.0 + lA*0.4, 35.0), 14.0));
    s = min(s, sdCapsule(q, vec2(-20.0, 70.0), vec2(-20.0 + lB*0.4, 35.0), 14.0));
    // Shin
    s = min(s, sdCapsule(q, vec2(  5.0 + lA*0.4, 35.0), vec2(  5.0 + lA, 0.0), 8.0));
    s = min(s, sdCapsule(q, vec2(-20.0 + lB*0.4, 35.0), vec2(-20.0 + lB, 0.0), 8.0));
    return s;
  }

  float dinoStego(vec2 q, float legPhase) {
    float s = 1e9;
    // Round body
    s = min(s, sdEllipse(q - vec2(0.0, 55.0), vec2(75.0, 30.0)));
    // Small head on short neck
    s = min(s, sdCapsule(q, vec2(55.0, 60.0), vec2(85.0, 75.0), 9.0));
    s = min(s, sdEllipse(q - vec2(92.0, 78.0), vec2(13.0, 9.0)));
    // Tail with spikes hint
    s = min(s, sdCapsule(q, vec2(-55.0, 55.0), vec2(-115.0, 70.0), 11.0));
    s = min(s, sdCapsule(q, vec2(-115.0, 70.0), vec2(-150.0, 78.0), 5.0));
    // Back plates (triangles approximated by tilted small capsules)
    for (int i = 0; i < 5; i++) {
      float fi = float(i);
      float bx = -40.0 + fi * 20.0;
      s = min(s, sdCapsule(q, vec2(bx, 78.0), vec2(bx + 6.0, 110.0), 5.0));
    }
    // Legs
    float p = legPhase;
    float fl = sin(p) * 6.0;
    float fr = sin(p + 3.14159) * 6.0;
    float bl = sin(p + 1.57) * 6.0;
    float br = sin(p - 1.57) * 6.0;
    s = min(s, sdCapsule(q, vec2( 35.0, 35.0), vec2( 35.0 + fl, 0.0), 9.0));
    s = min(s, sdCapsule(q, vec2( 15.0, 35.0), vec2( 15.0 + fr, 0.0), 9.0));
    s = min(s, sdCapsule(q, vec2(-20.0, 35.0), vec2(-20.0 + bl, 0.0), 9.0));
    s = min(s, sdCapsule(q, vec2(-40.0, 35.0), vec2(-40.0 + br, 0.0), 9.0));
    return s;
  }

  // Pterodactyl — flying, no ground contact
  float dinoPtero(vec2 q, float wingPhase) {
    float s = 1e9;
    float flap = sin(wingPhase) * 18.0;
    // Body
    s = min(s, sdEllipse(q, vec2(22.0, 9.0)));
    // Head/beak
    s = min(s, sdCapsule(q, vec2(15.0, 2.0), vec2(38.0, 6.0), 4.0));
    // Wings — V shape that flaps
    s = min(s, sdCapsule(q, vec2(0.0, 4.0), vec2(-50.0, 18.0 + flap), 5.0));
    s = min(s, sdCapsule(q, vec2(0.0, 4.0), vec2( 50.0, 18.0 + flap), 5.0));
    // Outer wing tips (further fold/extend)
    s = min(s, sdCapsule(q, vec2(-50.0, 18.0 + flap), vec2(-80.0, 4.0 + flap*0.6), 4.0));
    s = min(s, sdCapsule(q, vec2( 50.0, 18.0 + flap), vec2( 80.0, 4.0 + flap*0.6), 4.0));
    return s;
  }

  // Place and render a dinosaur given fragment pixel coord.
  // species: 0 sauropod, 1 trex, 2 stego, 3 ptero
  // Returns alpha in [0,1] and writes silhouette+rim into outCol.
  float renderDino(vec2 fragPx, vec2 anchorPx, float scale, float facing,
                   int species, float phase, out float rimAmt) {
    rimAmt = 0.0;
    vec2 q = (fragPx - anchorPx) / scale;
    q.x *= facing;
    float d;
    if (species == 0)      d = dinoSauropod(q, phase);
    else if (species == 1) d = dinoTRex(q, phase);
    else if (species == 2) d = dinoStego(q, phase);
    else                   d = dinoPtero(q, phase);
    // Convert SDF to alpha. d is in dino-local pixel units; account for scale
    // so the edge softness is constant on screen.
    float fw = max(fwidth(d), 0.5);
    float silhouette = 1.0 - smoothstep(-fw, fw, d);
    // Rim highlight just outside silhouette edge (on the lit side: top-left).
    float rim = (1.0 - smoothstep(-fw*0.5, fw*2.5, d)) - silhouette;
    rimAmt = clamp(rim, 0.0, 1.0);
    return silhouette;
  }

  // ---------- Mountain ridge as a height field ----------
  float mountainHeight(float x, float seed, float amp, float scale) {
    float h = fbm(vec2(x * scale, seed));
    h = pow(h, 1.4);
    return h * amp;
  }

  void main() {
    float aspect = uResolution.x / uResolution.y;
    vec2 fragPx = vUv * uResolution;
    float W = uResolution.x;
    float H = uResolution.y;

    // -------- sky gradient (3-stop) --------
    float t = vUv.y;
    vec3 sky;
    if (t < 0.5) sky = mix(uSkyBot, uSkyMid, t * 2.0);
    else         sky = mix(uSkyMid, uSkyTop, (t - 0.5) * 2.0);

    // -------- sun --------
    vec2 sunPx = vec2(W * 0.62, H * 0.55);
    float sunR = H * 0.18;
    float sunD = length(fragPx - sunPx);
    float sunDisc = smoothstep(sunR, sunR - 1.5, sunD);
    float sunHalo = exp(-sunD / (sunR * 1.6)) * 0.7;
    sky = mix(sky, uSunColor, sunDisc);
    sky += uSunColor * sunHalo * 0.55;

    // -------- distant mountain ridge --------
    float horizonY = H * 0.42;
    float farRidgeAmp = H * 0.10 * uMountains;
    float farH = mountainHeight(fragPx.x, 1.7, farRidgeAmp, 0.0008);
    float farTop = horizonY + farRidgeAmp * 0.6 + farH;
    float farMask = smoothstep(farTop + 1.5, farTop - 1.5, fragPx.y);
    sky = mix(sky, uMountainFar, farMask);

    // -------- mid mountain ridge --------
    float midRidgeAmp = H * 0.16 * uMountains;
    float midH = mountainHeight(fragPx.x + 1234.0, 5.3, midRidgeAmp, 0.0014);
    float midTop = horizonY + midRidgeAmp * 0.3 + midH;
    float midMask = smoothstep(midTop + 1.5, midTop - 1.5, fragPx.y);
    sky = mix(sky, uMountainNear, midMask);

    // -------- ground --------
    float groundY = H * 0.32;
    float groundMask = smoothstep(groundY + 1.0, groundY - 1.0, fragPx.y);
    sky = mix(sky, uGround, groundMask);

    // -------- dinosaurs --------
    int N = int(clamp(uDinoCount, 0.0, 12.0));
    float Nf = max(float(N), 1.0);
    vec3 col = sky;

    // Walk drift in pixels (loops across full width + dino width)
    float walkPx = uTime * uWalkSpeed * 22.0;
    float wrap = W + 600.0;

    for (int i = 0; i < 12; i++) {
      if (i >= N) break;
      float fi = float(i);
      // Per-dino seed
      float h0 = hash11(fi * 1.37 + 0.3);
      float h1 = hash11(fi * 2.71 + 7.1);
      float h2 = hash11(fi * 4.13 + 2.4);
      // Stratified base x across full canvas
      float laneX = ((fi + 0.5) / Nf + (h0 - 0.5) * 0.6 / Nf) * W;
      // Direction: 70% facing right
      float facing = (h1 < 0.7) ? 1.0 : -1.0;
      // Walking position (wrapping)
      float x = mod(laneX + walkPx * facing + wrap, wrap);
      // Species pick
      int species;
      if (h2 < 0.30)      species = 0; // sauropod
      else if (h2 < 0.55) species = 1; // trex
      else if (h2 < 0.80) species = 2; // stego
      else                species = 3; // ptero
      // Scale — slight variance + height-relative so all aspect ratios behave.
      // Base dino height is ~240 dino-units; aim for ~28% of canvas height.
      float baseScale = (H * 0.28) / 240.0 * uDinoScale;
      float scale = baseScale * mix(0.75, 1.15, h0);

      // Vertical anchor on the ground for walking dinos; in the sky for ptero.
      float anchorY = groundY;
      if (species == 3) {
        // Pterodactyl bob
        anchorY = horizonY + H * (0.18 + h0 * 0.08) + sin(uTime * 0.8 + fi) * 4.0;
      } else {
        // Tiny vertical bob with stride
        anchorY += sin(uTime * uWalkSpeed * 4.0 + fi) * 1.2;
      }

      vec2 anchor = vec2(x, anchorY);
      float phase = uTime * (species == 3 ? 6.0 : 4.0) * uWalkSpeed + fi * 1.7;
      float rim;
      float a = renderDino(fragPx, anchor, scale, facing, species, phase, rim);

      // Ptero behind mountains? No — fly above. Walking dinos in front of mid mountains.
      col = mix(col, uDinoColor, a);
      col += uRimColor * rim * 0.65;
    }

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class DinoParade extends Generator {
  constructor() {
    super({
      name: 'Dino Parade',
      category: 'ambient',
      schema: {
        walkSpeed:   { default: 1.0, min: 0.0, max: 4.0,  step: 0.05, label: 'Walk speed' },
        dinoCount:   { default: 7,   min: 1,   max: 12,   step: 1,    label: 'Dino count' },
        dinoScale:   { default: 1.0, min: 0.4, max: 2.0,  step: 0.05, label: 'Dino scale' },
        mountains:   { default: 1.0, min: 0.0, max: 2.0,  step: 0.05, label: 'Mountain height' },
        skyTop:      { default: '#1a1040', type: 'color', label: 'Sky top' },
        skyMid:      { default: '#d24a30', type: 'color', label: 'Sky mid' },
        skyBot:      { default: '#f7c37a', type: 'color', label: 'Sky bottom' },
        sunColor:    { default: '#ffd089', type: 'color', label: 'Sun color' },
        mountainFar: { default: '#3a2150', type: 'color', label: 'Mountain (far)' },
        mountainNear:{ default: '#1c0c2c', type: 'color', label: 'Mountain (near)' },
        ground:      { default: '#0a0414', type: 'color', label: 'Ground' },
        dinoColor:   { default: '#040208', type: 'color', label: 'Dino silhouette' },
        rimColor:    { default: '#ffb070', type: 'color', label: 'Rim light' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      extensions: { derivatives: true },
      uniforms: {
        uTime:         { value: 0 },
        uResolution:   { value: new THREE.Vector2(engine.width, engine.height) },
        uWalkSpeed:    { value: this.v('walkSpeed') },
        uDinoScale:    { value: this.v('dinoScale') },
        uDinoCount:    { value: this.v('dinoCount') },
        uMountains:    { value: this.v('mountains') },
        uSkyTop:       { value: new THREE.Color(this.v('skyTop')) },
        uSkyMid:       { value: new THREE.Color(this.v('skyMid')) },
        uSkyBot:       { value: new THREE.Color(this.v('skyBot')) },
        uSunColor:     { value: new THREE.Color(this.v('sunColor')) },
        uMountainFar:  { value: new THREE.Color(this.v('mountainFar')) },
        uMountainNear: { value: new THREE.Color(this.v('mountainNear')) },
        uGround:       { value: new THREE.Color(this.v('ground')) },
        uDinoColor:    { value: new THREE.Color(this.v('dinoColor')) },
        uRimColor:     { value: new THREE.Color(this.v('rimColor')) },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    switch (key) {
      case 'walkSpeed':    u.uWalkSpeed.value = val; break;
      case 'dinoCount':    u.uDinoCount.value = val; break;
      case 'dinoScale':    u.uDinoScale.value = val; break;
      case 'mountains':    u.uMountains.value = val; break;
      case 'skyTop':       u.uSkyTop.value.set(val); break;
      case 'skyMid':       u.uSkyMid.value.set(val); break;
      case 'skyBot':       u.uSkyBot.value.set(val); break;
      case 'sunColor':     u.uSunColor.value.set(val); break;
      case 'mountainFar':  u.uMountainFar.value.set(val); break;
      case 'mountainNear': u.uMountainNear.value.set(val); break;
      case 'ground':       u.uGround.value.set(val); break;
      case 'dinoColor':    u.uDinoColor.value.set(val); break;
      case 'rimColor':     u.uRimColor.value.set(val); break;
    }
  }

  update(dt, time) {
    if (this.mesh) this.mesh.material.uniforms.uTime.value = time;
  }
}
